import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../booking/api";
import { isoDate, parseIso } from "../../booking/utils";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CalendarDays } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function BookingsPage() {
  const [from, setFrom] = useState(isoDate(new Date()));
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/api/admin/statuses").then((r) => setStatuses(r.data));
    api.get("/api/admin/resource-groups").then((r) => setGroups(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/api/admin/bookings", { params: { date_from: from || undefined, q: q || undefined } })
        .then((r) => setRows(r.data)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [from, q]);

  const update = async (b, patch) => {
    try {
      const r = await api.put(`/api/admin/bookings/${b.id}`, patch);
      setRows((rs) => rs.map((x) => (x.id === b.id ? r.data : x)));
      toast.success(`${b.code} updated`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refund = async (b) => {
    if (!window.confirm(`Refund €${b.deposit_amount} deposit for ${b.code}?`)) return;
    try {
      const r = await api.post(`/api/admin/bookings/${b.id}/refund`);
      setRows((rs) => rs.map((x) => (x.id === b.id ? r.data : x)));
      toast.success(`${b.code} refunded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-bookings-page">
      <h1 className="text-2xl font-semibold">Bookings</h1>
      <div className="flex flex-wrap gap-3 mt-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-48 justify-start font-normal" data-testid="bookings-filter-from">
              <CalendarDays size={15} className="mr-2" /> {from ? `From ${from}` : "All dates"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" weekStartsOn={1} selected={from ? parseIso(from) : undefined}
              onSelect={(d) => setFrom(d ? isoDate(d) : "")} />
          </PopoverContent>
        </Popover>
        <Input placeholder="Search name, email, phone, code" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="bookings-search" />
      </div>
      <div className="bg-white rounded-lg border mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Code</TableHead><TableHead>Service</TableHead>
              <TableHead>Guest</TableHead><TableHead>Pax</TableHead><TableHead>Deposit</TableHead><TableHead>Spot</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows?.map((b) => (
              <TableRow key={b.id} data-testid={`booking-row-${b.code}`} className={b.status === "Cancelled" ? "opacity-50" : ""}>
                <TableCell className="whitespace-nowrap">{b.date}</TableCell>
                <TableCell>{b.time}</TableCell>
                <TableCell className="font-mono text-xs">{b.code}</TableCell>
                <TableCell className="max-w-[200px] truncate">{b.service_name}</TableCell>
                <TableCell>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.phone} · {b.email}</div>
                  {b.notes && <div className="text-xs text-amber-700 mt-1">“{b.notes}”</div>}
                </TableCell>
                <TableCell>{b.guests}</TableCell>
                <TableCell className="text-xs whitespace-nowrap" data-testid={`booking-payment-${b.code}`}>
                  {b.deposit_amount > 0 ? `€${b.deposit_amount} · ${b.payment_status}` : "—"}
                  {b.refund_status === "failed" && <div className="text-red-600" title={b.refund_error}>refund failed</div>}
                  {b.payment_status === "paid" && (
                    <button onClick={() => refund(b)} data-testid={`booking-refund-${b.code}`}
                      className="block mt-1 text-red-700 underline hover:no-underline">Refund</button>
                  )}
                </TableCell>
                <TableCell>
                  {b.resource_group_id ? (
                    <select value={b.resource_unit || ""} onChange={(e) => update(b, { resource_unit: e.target.value })}
                      data-testid={`booking-unit-${b.code}`} className="border rounded px-2 py-1 text-xs bg-white">
                      {groups.find((g) => g.id === b.resource_group_id)?.units.filter((u) => u.active).map((u) => (
                        <option key={u.name} value={u.name}>{u.name}</option>
                      ))}
                    </select>
                  ) : "—"}
                </TableCell>
                <TableCell>
                  <select value={b.status} onChange={(e) => update(b, { status: e.target.value })}
                    data-testid={`booking-status-${b.code}`} className="border rounded px-2 py-1 text-xs bg-white">
                    {statuses.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </TableCell>
              </TableRow>
            ))}
            {rows?.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-10" data-testid="bookings-empty">No bookings found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
